import { useCallback, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { TeamMembersSection } from '@/components/company/TeamMembersSection';
import { InviteCompanyMemberDialog } from '@/components/company/InviteCompanyMemberDialog';

export function CompanyManagersPage() {
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  const { canManageTeam } = useCompanyRole();
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const members = useQuery(
    api.companies.members.list,
    currentCompany ? { companyId: currentCompany._id } : 'skip'
  );
  const invitations = useQuery(
    api.companies.invitations.listByCompany,
    currentCompany ? { companyId: currentCompany._id } : 'skip'
  );
  const removeMember = useMutation(api.companies.members.remove);
  const cancelInvitation = useMutation(api.companies.invitations.cancelInvitation);
  const updateMemberName = useMutation(api.users.mutations.updateMemberName);

  const pendingInvitations = (invitations ?? []).filter(
    (inv: { status: string }) => inv.status === 'pending'
  );

  const handleRemoveMember = useCallback(
    async (memberId: Id<'companyMembers'>) => {
      try {
        await removeMember({ memberId });
        toast.success('Member removed');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to remove member');
      }
    },
    [removeMember]
  );

  const handleUpdateMemberName = useCallback(
    async (targetUserId: Id<'users'>, name: string) => {
      try {
        await updateMemberName({ targetUserId, name });
        toast.success('Name updated');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update name');
      }
    },
    [updateMemberName]
  );

  const handleCancelInvitation = useCallback(
    async (invitationId: Id<'companyInvitations'>) => {
      try {
        await cancelInvitation({ invitationId });
        toast.success('Invitation cancelled');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to cancel invitation');
      }
    },
    [cancelInvitation]
  );

  if (companyLoading) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Managers</h1>

      <TeamMembersSection
        members={members}
        pendingInvitations={canManageTeam ? pendingInvitations : undefined}
        onInvite={canManageTeam ? () => setIsInviteOpen(true) : undefined}
        onRemove={canManageTeam ? handleRemoveMember : undefined}
        onUpdateMemberName={canManageTeam ? handleUpdateMemberName : undefined}
        onCancelInvitation={canManageTeam ? handleCancelInvitation : undefined}
      />

      {currentCompany && (
        <InviteCompanyMemberDialog
          open={isInviteOpen}
          onOpenChange={setIsInviteOpen}
          companyId={currentCompany._id}
        />
      )}
    </div>
  );
}
